import styled from 'styled-components';

import { Button } from '../../cdl/Button/Button';
import { Text } from '../../cdl/Text/Text';
import { ButtonGroup } from '../buttons/ButtonGroup';
import { translate } from '../helpers/translate.helper';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import { useOpenFreshchat } from '../hooks/useOpenFreshchat';
import { useRole } from '../hooks/useRole';
import { IconCopy } from '../icons/cdl/Copy';
import { IconReload } from '../icons/cdl/Reload';
import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

// We can't use the values from our theme in here, because this error component is used in the root route

const StyledPre = styled.pre`
    font-variant-numeric: lining-nums tabular-nums stacked-fractions slashed-zero;
    font-family:
        Roboto Mono,
        monospace;
    font-size: 12px;
    font-style: normal;
    font-weight: ${theme.fontWeights[400]};
    line-height: normal;

    color: #5f7d8b; // foreground.subtle
    border: none;
    background: none;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px;
`;

const CopyButton = styled(Button).attrs({
    emphasis: 'high',
    LeadingIcon: IconCopy,
})`
    position: absolute;
    bottom: 8px;
    right: 8px;
`;

interface AdvancedErrorProps {
    error?: unknown;
    stack?: string;
    resetError?: () => void;
    variant?: 'contained' | 'fluid';
}

export const AdvancedError = ({
    error,
    stack,
    resetError = () => window.location.reload(),
    variant = 'contained',
}: AdvancedErrorProps) => {
    const role = useRole();
    const openFreshchat = useOpenFreshchat();
    const copyToClipboard = useCopyToClipboard();

    const cleanedError = error ? error.toString().replace(/^Error:\s*/, '') : undefined;

    let errorMessage = '';

    if (cleanedError) {
        errorMessage = cleanedError;
    }

    if (stack) {
        errorMessage += stack;
    }

    const isFluid = variant === 'fluid';

    return (
        <Box
            display="flex"
            justifyContent="center"
            paddingTop={isFluid ? '128px' : '256px'}
            paddingX={isFluid ? 32 : undefined}
            flexWrap={isFluid ? 'wrap' : undefined}
        >
            <Box
                display={isFluid ? 'flex' : 'grid'}
                flexDirection={isFluid ? 'column' : undefined}
                gap={32}
                width={isFluid ? '100%' : '560px'}
                maxWidth={isFluid ? '100%' : undefined}
            >
                <Box display={isFluid ? 'flex' : 'grid'} flexDirection={isFluid ? 'column' : undefined} gap={16}>
                    <Text variant="display">{translate('errorView.title')}</Text>
                    <Text variant="body" color="#466573">
                        {translate('errorView.description')}
                    </Text>
                </Box>

                {cleanedError || stack ? (
                    <div>
                        <Text variant="fieldLabel" color="#466573">
                            {translate('errorView.errorMessage')}
                        </Text>
                        <Box
                            position="relative"
                            height="84px"
                            width={isFluid ? undefined : '560px'}
                            overflow="hidden"
                            backgroundColor="#F8FBFD" //background.subtle
                            borderRadius="4px"
                            border="1px solid"
                            borderColor="#CEE2EA" // border.muted
                        >
                            <StyledPre>
                                {cleanedError ? cleanedError.toString() : null}
                                {stack || null}
                            </StyledPre>
                            <CopyButton onClick={() => copyToClipboard(errorMessage)}>
                                {translate('errorView.copy')}
                            </CopyButton>
                        </Box>
                    </div>
                ) : null}

                <ButtonGroup>
                    <Button emphasis="high" onClick={resetError} LeadingIcon={IconReload}>
                        {translate('errorView.reloadPage')}
                    </Button>

                    {!role?.isSuperAdmin() ? (
                        <Button emphasis="low" onClick={openFreshchat}>
                            {translate('errorView.getHelp')}
                        </Button>
                    ) : null}
                </ButtonGroup>
            </Box>
        </Box>
    );
};
